import type {CoreLogger, DocumentManager} from '@wix/document-manager-core'
import {
    constants,
    DataMigrationRunner,
    extensions,
    FetchFn,
    LocalEditorExtensionAPI,
    siteDataImmutableFromSnapshot,
    type FixersExtensionAPI,
    type CSaveApi,
    type ServiceTopology,
    type SnapshotExtApi,
    type MigratorsSettings,
    type SantaDataFixersSettings
} from '@wix/document-manager-extensions'
import type {DataFixer, Experiment, PageListWithMasterAndMainPage, DSConfig} from '@wix/document-services-types'
import _ from 'lodash'
import {INTERACTIONS} from '../constants/constants'
import type {RendererModelBuilderForHost} from '../host'
import type {FetchPagesFacade} from '../types'
import {buildPageList} from '../utils/pageListUtils'
import type {
    DocumentServicesModel,
    DocumentServicesModelForServer,
    RendererModel,
    RendererModelForServer
} from './modelTypes'
import multilingualInitializer from './multilingualInitializer'
import {movePageDataToMaster} from './pageDataMigrator'
import {convertStore, convertToFragmentStore} from './storeToDal'

const {SNAPSHOTS, RESOURCE_TYPES} = constants
const {getSiteDataJson} = siteDataImmutableFromSnapshot

const isExperimentOpen = (value: string) => !!(value && value !== 'old' && value !== 'false')

export interface InitParams {
    documentManager: DocumentManager
    dataFixer: DataFixer
    partialPages: string[]
    dataMigrationRunner: DataMigrationRunner
    santaDataFixersSettings: SantaDataFixersSettings
    migratorsSettings: MigratorsSettings
    rendererModelBuilder: RendererModelBuilderForHost
    serviceTopology: ServiceTopology
    documentServicesModel: DocumentServicesModelForServer | DocumentServicesModel
    config: DSConfig
    logger: CoreLogger
    fetchFn: FetchFn
    trackingFn<T>(...args: any[]): Promise<T>
    fetchPagesFacade: FetchPagesFacade
    experimentInstance: Experiment
    pageList: PageListWithMasterAndMainPage
}

export type FetchPagesToDalFunction = (pageIds: string[]) => Promise<void>

export interface InitResult {
    fetchPagesToDal: FetchPagesToDalFunction
}

export interface InitFromCacheParams {
    documentManager: DocumentManager
    partialPages: string[]
    logger: CoreLogger
    rendererModelBuilder: RendererModelBuilderForHost
    documentServicesModel: DocumentServicesModelForServer | DocumentServicesModel
    serviceTopology: ServiceTopology
    trackingFn<T>(...args: any[]): Promise<T>
}

export interface ServerStore {
    rendererModel: RendererModelForServer | RendererModel
    documentServicesModel: DocumentServicesModelForServer | DocumentServicesModel
    serviceTopology: ServiceTopology
    pagesData: Record<string, any>
    orphanPermanentDataNodes?: any[]
    routers?: any
    pagesPlatformApplications?: any
    origin?: string
}

const stubTrackingFn = async (name: string, fn: Function) => await fn()

const initialize = async ({
    documentManager,
    partialPages,
    serviceTopology,
    rendererModelBuilder,
    documentServicesModel,
    config,
    logger,
    fetchFn,
    fetchPagesFacade,
    pageList,
    trackingFn = stubTrackingFn,
    santaDataFixersSettings,
    migratorsSettings
}: InitParams): Promise<InitResult> => {
    const options = {
        paramsOverrides: {
            isDraft: _.get(documentServicesModel, ['isDraft'])
        }
    }

    let serverStoreBase = {} as ServerStore

    const interactionStartedWithOptions = (name: string) => logger.interactionStarted(name, options)
    const interactionEndedWithOptions = (name: string) => logger.interactionEnded(name, options)
    async function trackingAndInteraction<T>(name: string, fn: (...args: any[]) => Promise<T>): Promise<T> {
        interactionStartedWithOptions(name)
        const returnValue: T = await trackingFn(name, async () => await fn())
        interactionEndedWithOptions(name)

        return returnValue
    }
    const minorFixing = (pageJson: any, pageId: string) => {
        if (!pageJson.structure.id && pageId === 'masterPage') {
            pageJson.structure.id = 'masterPage'
        }
        return pageJson
    }

    const createServerStore = async (serverStorePagesData: Record<string, any>): Promise<ServerStore> => {
        // Setup
        const serverStore = {
            ...serverStoreBase,
            pagesData: serverStorePagesData
        }

        _(serverStore.pagesData)
            .omit(['masterPage'])
            .forEach(page => movePageDataToMaster(page, serverStore.pagesData.masterPage))

        _.forEach(serverStore.pagesData, minorFixing)

        const initialDocument = documentManager.experimentInstance.isOpen('dm_fragments')
            ? convertToFragmentStore(serverStore)
            : convertStore(serverStore)
        const storeLabel = 'create-store'

        documentManager.dal.mergeToApprovedStore(initialDocument, storeLabel)

        return serverStore
    }

    const buildServerStore = async (rendererModel: any, schemasLoadedPromise: Promise<void>): Promise<ServerStore> => {
        interactionStartedWithOptions(INTERACTIONS.LOAD_PAGE_PAYLOADS)

        const pageListToLoad = buildPageList(pageList, partialPages)

        const pagesData = await trackingAndInteraction(INTERACTIONS.FETCH_PAGES, async () =>
            fetchPagesFacade.fetchPages(fetchFn, pageListToLoad)
        )

        serverStoreBase = {
            rendererModel,
            documentServicesModel,
            serviceTopology,
            orphanPermanentDataNodes: [] as any[],
            routers: rendererModel.routers,
            pagesPlatformApplications: rendererModel.pagesPlatformApplications
        } as ServerStore

        interactionEndedWithOptions(INTERACTIONS.LOAD_PAGE_PAYLOADS)

        await schemasLoadedPromise
        const serverStore = await trackingAndInteraction(INTERACTIONS.CREATE_SERVER_STORE, async () =>
            createServerStore(pagesData)
        )

        return serverStore
    }

    const initCSave = async (serverStore: ServerStore): Promise<ServerStore> => {
        const {snapshots} = documentManager.extensionAPI as SnapshotExtApi

        const {continuousSave} = documentManager.extensionAPI as CSaveApi
        // apply csave transactions and take csave snapshot
        const changedApplied = await trackingAndInteraction(INTERACTIONS.INIT_CSAVE, async () =>
            continuousSave.initCSave(partialPages)
        )

        if (!changedApplied) {
            return serverStore
        }

        // rebuild a server store (use site data immutable)
        const store = getSiteDataJson(
            snapshots.getLastSnapshotByTagName(extensions.continuousSave.CSAVE_TAG),
            config.origin,
            {
                withPagesData: true
            }
        ) as ServerStore
        store.rendererModel.pagesPlatformApplications = store.pagesPlatformApplications
        return store
    }

    const initCSaveIfNeeded = async (serverStore: ServerStore): Promise<ServerStore> => {
        const {continuousSave} = documentManager.extensionAPI as CSaveApi
        if (continuousSave && config.continuousSave) {
            return await initCSave(serverStore)
        }
        return serverStore
    }
    const runFixersIfNeeded = async (): Promise<void> => {
        const {fixers, snapshots} = documentManager.extensionAPI as FixersExtensionAPI & SnapshotExtApi

        interactionStartedWithOptions(INTERACTIONS.RUN_FIXERS)

        snapshots.takeSnapshot(SNAPSHOTS.BEFORE_FIXERS)

        await fixers.fixAllPagesAndMigrateSite(partialPages, santaDataFixersSettings, migratorsSettings)

        snapshots.takeSnapshot(SNAPSHOTS.AFTER_FIXERS)

        interactionEndedWithOptions(INTERACTIONS.RUN_FIXERS)
    }

    const localEditorInitialization = async (): Promise<InitResult> => {
        documentManager.initializeModels({
            rendererModel: await rendererModelBuilder.getRendererModel(),
            documentServicesModel,
            serviceTopology
        })

        const {localEditor} = documentManager.extensionAPI as LocalEditorExtensionAPI
        await localEditor.initialize()

        await runFixersIfNeeded()

        return {fetchPagesToDal: () => Promise.resolve()}
    }

    const main = async (): Promise<InitResult> => {
        if (config.localEditor) {
            return localEditorInitialization()
        }

        interactionStartedWithOptions(INTERACTIONS.DM_LOAD_ALL_DATA)

        const rendererModel = await rendererModelBuilder.getRendererModel()
        documentManager.initializeModels({
            rendererModel,
            documentServicesModel,
            serviceTopology
        })
        if (
            config.useResourceLoaderForInit || //temporary until dm_useResourceLoaderForInit is 100% and merged
            isExperimentOpen(rendererModel.runningExperiments.dm_useResourceLoaderForInit)
        ) {
            //TODO: fix this flow with new lifecycle method or dependencies
            // schema needs to load before setting pages/fragments, but ideally should not block the requests
            await documentManager.resourceLoader.loadAll(RESOURCE_TYPES.SCHEMA)
            await documentManager.resourceLoader.loadInitialResources()
            const pagesToLoad = _.isEmpty(partialPages) ? undefined : partialPages
            if (pagesToLoad) {
                await documentManager.resourceLoader.load(RESOURCE_TYPES.PAGE, pagesToLoad)
            } else {
                await documentManager.resourceLoader.loadAll(RESOURCE_TYPES.PAGE)
            }
            documentManager.initializeChannelSubscriptions()
        } else {
            const schemasLoadedPromise = documentManager.resourceLoader.loadAll(RESOURCE_TYPES.SCHEMA)
            // we need to load schemas before setting pages to dal.
            // but we will pass on the promise and only await before setting to dal,
            // so that requests to schemas and page statics can run in parallel
            const serverStore = await buildServerStore(rendererModel, schemasLoadedPromise)
            await documentManager.resourceLoader.loadInitialResources()
            const {snapshots} = documentManager.extensionAPI as SnapshotExtApi
            snapshots.takeSnapshot(SNAPSHOTS.DAL_INITIAL)
            documentManager.initializeChannelSubscriptions()
            const newStore = await initCSaveIfNeeded(serverStore)
            documentManager.dal.commitTransaction('before-fixers', true)
            if (isExperimentOpen(newStore.rendererModel.runningExperiments.dm_moveValidationBaseline)) {
                documentManager.dal.createValidationBaseline()
            }
            await runFixersIfNeeded()
        }

        // Applying multilingual overrides
        if (!config.keepMultiLingualModelsFromServer) {
            await trackingAndInteraction(INTERACTIONS.MULTILINGUAL_INIT, async () =>
                multilingualInitializer.initialize(documentManager)
            )
        }

        // Closing the transaction
        await trackingAndInteraction(INTERACTIONS.INIT_FINAL_COMMIT, async () =>
            documentManager.dal.commitTransaction('mainInitialization', true)
        )

        interactionEndedWithOptions(INTERACTIONS.DM_LOAD_ALL_DATA)

        const fetchPagesToDal = (() => {
            const loadedPages = new Set(partialPages)
            return async (pageIds: string[]) => {
                if (
                    config.useResourceLoaderForInit || //temporary until dm_useResourceLoaderForInit is 100% and merged
                    isExperimentOpen(rendererModel.runningExperiments.dm_useResourceLoaderForInit)
                ) {
                    return await documentManager.resourceLoader.load(RESOURCE_TYPES.PAGE, pageIds)
                }
                const pageIdsToFetch = _.reject(pageIds, pageId => loadedPages.has(pageId))
                if (_.isEmpty(pageIdsToFetch)) {
                    return
                }

                _.forEach(pageIdsToFetch, pageId => loadedPages.add(pageId))
                const pageListToLoad = buildPageList(pageList, pageIdsToFetch)
                const rawPagesData = await fetchPagesFacade.fetchPages(fetchFn, pageListToLoad)
                const filteredPagesData = _.pickBy(
                    rawPagesData,
                    (value, pageId) => pageIdsToFetch.includes(pageId) || pageId === 'masterPage'
                )
                const store = {...serverStoreBase, pagesData: filteredPagesData}
                let changes
                if (isExperimentOpen(store.rendererModel.runningExperiments.dm_fragments)) {
                    changes = convertToFragmentStore(store)
                    changes.forEach((fragmentStore, fragmentType) => {
                        fragmentStore.forEach((p, v) => {
                            const fragmentPointer = {...p, fragmentType}
                            documentManager.dal.set(fragmentPointer, v)
                        })
                    })
                } else {
                    changes = convertStore(store)
                    changes.forEach((p, v) => {
                        documentManager.dal.set(p, v)
                    })
                }
                for (const pageId of pageIdsToFetch) {
                    const {fixers} = documentManager.extensionAPI as FixersExtensionAPI
                    fixers.fixPage(pageId, santaDataFixersSettings, migratorsSettings)
                }
            }
        })()

        return {fetchPagesToDal}
    }

    return await main()
}

export {initialize}
